import type { ReactNode } from 'react';
import { type FieldValues, FormProvider, type UseFormReturn } from 'react-hook-form';

import { Drawer, type DrawerProps } from './drawer';

/** `TOut` is the resolver's parsed output, when a schema transforms (e.g. a `nullable` field a
 *  refine narrows); it defaults to the form values for every caller that does not. */
export type FormDrawerProps<T extends FieldValues, TOut extends FieldValues = T> = Omit<
  DrawerProps,
  'onSubmit' | 'primaryText' | 'onPrimary' | 'primaryIsSubmit' | 'children'
> & {
  form: UseFormReturn<T, unknown, TOut>;
  onSubmit: (values: TOut) => void | Promise<void>;
  submitText?: string;
  resetOnClose?: boolean;
  children: ReactNode;
};

export function FormDrawer<T extends FieldValues, TOut extends FieldValues = T>({
  form,
  onSubmit,
  submitText = 'Apply',
  resetOnClose = true,
  onClose,
  loading,
  maskClosable,
  children,
  ...rest
}: FormDrawerProps<T, TOut>) {
  const isDirty = form.formState.isDirty;
  const handleClose = () => {
    if (resetOnClose) form.reset();
    onClose();
  };

  return (
    <Drawer
      {...rest}
      onClose={handleClose}
      maskClosable={maskClosable ?? !isDirty}
      loading={loading || form.formState.isSubmitting}
      primaryText={submitText}
      primaryIsSubmit
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <FormProvider {...form}>{children}</FormProvider>
    </Drawer>
  );
}
